import type { SetScorePayload } from '../api/competitionApi';
import type { ApiBracket, ApiMatch, ApiScoringRules, DisciplineCode } from './types';

/**
 * Model formularza wprowadzania wyniku meczu.
 *
 * Walidacja odzwierciedla walidatory backendu (`TennisScoringValidator`,
 * `SquashScoringValidator`, `BadmintonScoringValidator`), żeby użytkownik
 * dostawał polskie komunikaty przed wysłaniem żądania — backend i tak
 * waliduje ponownie.
 */

/** Jeden wiersz formularza — wartości surowe z inputów (puste = set nierozegrany). */
export interface SetRowDraft {
  score1: string;
  score2: string;
  tiebreak1: string;
  tiebreak2: string;
}

/** Pusty wiersz seta. */
export const EMPTY_SET_ROW: SetRowDraft = { score1: '', score2: '', tiebreak1: '', tiebreak2: '' };

/** Zasady punktacji z bezpiecznymi domyślnymi (mecz bez reguł = best of 3, sety do 6). */
export interface ScoreRules {
  bestOf: number;
  superTiebreak: boolean;
  target: number;
}

/** Wyciąga zasady punktacji meczu; brak reguł traktuje jak standardowy tenis. */
export function rulesOf(rules: ApiScoringRules | null): ScoreRules {
  return {
    bestOf: rules?.bestOf ?? 3,
    superTiebreak: rules?.superTiebreak ?? false,
    target: rules?.shortSetTarget ?? 6,
  };
}

/** Liczba setów potrzebna do wygrania meczu. */
export function setsToWin(bestOf: number): number {
  return Math.floor(bestOf / 2) + 1;
}

/** Początkowe wiersze formularza — wypełnione wynikami meczu przy edycji. */
export function initialRows(match: ApiMatch): SetRowDraft[] {
  const bestOf = rulesOf(match.scoringRules).bestOf;
  return Array.from({ length: bestOf }, (_, index) => {
    const set = match.setScores.find((item) => item.setNumber === index + 1);
    if (!set) return { ...EMPTY_SET_ROW };
    return {
      score1: String(set.score1),
      score2: String(set.score2),
      tiebreak1: set.tiebreakScore1 !== null ? String(set.tiebreakScore1) : '',
      tiebreak2: set.tiebreakScore2 !== null ? String(set.tiebreakScore2) : '',
    };
  });
}

/** Czy wiersz jest częściowo albo w pełni wypełniony. */
function isRowTouched(row: SetRowDraft): boolean {
  return row.score1.trim() !== '' || row.score2.trim() !== '';
}

/** Parsuje pole liczbowe; `null` dla pustego, `NaN` dla śmieci. */
function parseField(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return Number(trimmed);
}

/** Wypełnione wiersze od początku formularza — do walidacji i ładunku. */
export function filledRows(rows: readonly SetRowDraft[]): SetRowDraft[] {
  const filled: SetRowDraft[] = [];
  for (const row of rows) {
    if (!isRowTouched(row)) break;
    filled.push(row);
  }
  return filled;
}

/** Zwycięzca seta: `1`, `2` albo `null` (niekompletny / remis). */
export function setWinner(row: SetRowDraft): 1 | 2 | null {
  const s1 = parseField(row.score1);
  const s2 = parseField(row.score2);
  if (s1 === null || s2 === null || Number.isNaN(s1) || Number.isNaN(s2)) return null;
  if (s1 > s2) return 1;
  if (s2 > s1) return 2;
  return null;
}

/**
 * Zwycięzca *potwierdzonego* seta — zwraca stronę tylko wtedy, gdy wiersz jest
 * kompletny i poprawny wg reguł dyscypliny. W odróżnieniu od `setWinner` nie uznaje
 * za wygrany seta wpisywanego w trakcie (np. 3:4 przy setach do 6), więc formularz
 * może na tym oprzeć ptaszek, podświetlenie i wynik w setach.
 */
export function confirmedSetWinner(
  rows: readonly SetRowDraft[],
  index: number,
  rules: ScoreRules,
  discipline: DisciplineCode,
): 1 | 2 | null {
  const row = rows[index];
  if (!row) return null;
  const winner = setWinner(row);
  if (winner === null) return null;
  return validateRow(rows, index, rules, discipline) === null ? winner : null;
}

/** Sety wygrane przez obie strony — liczone tylko z potwierdzonych, poprawnych setów. */
export function setsWonOf(
  rows: readonly SetRowDraft[],
  rules: ScoreRules,
  discipline: DisciplineCode,
): [number, number] {
  let won1 = 0;
  let won2 = 0;
  const filled = filledRows(rows);
  for (let index = 0; index < filled.length; index += 1) {
    const winner = confirmedSetWinner(filled, index, rules, discipline);
    if (winner === 1) won1 += 1;
    if (winner === 2) won2 += 1;
  }
  return [won1, won2];
}

/**
 * Czy dany wiersz jest decydującym setem granym jako super tie-break —
 * backend uznaje za niego set przy remisie setów jednego przed końcem.
 */
export function isSuperTiebreakRow(
  rows: readonly SetRowDraft[],
  index: number,
  rules: ScoreRules,
): boolean {
  if (!rules.superTiebreak) return false;
  const needed = setsToWin(rules.bestOf);
  let won1 = 0;
  let won2 = 0;
  for (let i = 0; i < index; i += 1) {
    const winner = setWinner(rows[i]!);
    if (winner === 1) won1 += 1;
    if (winner === 2) won2 += 1;
  }
  return won1 === needed - 1 && won2 === needed - 1;
}

/**
 * Czy wynik seta wymaga podania punktów tie-breaka (tenis/padel:
 * `target+1 : target`, np. 7:6).
 */
export function needsTiebreak(
  row: SetRowDraft,
  rules: ScoreRules,
  discipline: DisciplineCode,
): boolean {
  if (discipline !== 'TENNIS' && discipline !== 'PADEL') return false;
  const s1 = parseField(row.score1);
  const s2 = parseField(row.score2);
  if (s1 === null || s2 === null || Number.isNaN(s1) || Number.isNaN(s2)) return false;
  const winner = Math.max(s1, s2);
  const loser = Math.min(s1, s2);
  return winner === rules.target + 1 && loser === rules.target;
}

/* --- Walidacja per dyscyplina --- */

/** Zwykły set tenisa/padla — lustrzane odbicie `validateRegularSet`. */
function validateTennisSet(row: SetRowDraft, setNumber: number, target: number): string | null {
  const s1 = parseField(row.score1);
  const s2 = parseField(row.score2);
  if (s1 === null || s2 === null || Number.isNaN(s1) || Number.isNaN(s2) || s1 < 0 || s2 < 0) {
    return `Set ${setNumber}: uzupełnij wynik obu stron.`;
  }
  const winner = Math.max(s1, s2);
  const loser = Math.min(s1, s2);

  if (winner < target) return `Set ${setNumber}: zwycięzca musi wygrać co najmniej ${target} gemów.`;
  if (winner === target && loser <= target - 2) return null;
  if (winner === target + 1 && loser === target - 1) return null;
  if (winner === target + 1 && loser === target) {
    // 7:6 — wymagany tie-break.
    const tb1 = parseField(row.tiebreak1);
    const tb2 = parseField(row.tiebreak2);
    if (tb1 === null || tb2 === null || Number.isNaN(tb1) || Number.isNaN(tb2)) {
      return `Set ${setNumber}: przy wyniku ${s1}:${s2} podaj punkty tie-breaka.`;
    }
    const tbWinner = Math.max(tb1, tb2);
    const tbLoser = Math.min(tb1, tb2);
    if (tbWinner < 7) return `Set ${setNumber}: zwycięzca tie-breaka musi zdobyć co najmniej 7 punktów.`;
    if (tbWinner - tbLoser < 2 && tbWinner !== 7) {
      return `Set ${setNumber}: tie-break wygrywa się dwoma punktami przewagi.`;
    }
    if (tbWinner === 7 && tbLoser > 5) {
      return `Set ${setNumber}: przy 7 punktach zwycięzcy przegrany ma najwyżej 5.`;
    }
    if (tb1 > tb2 !== s1 > s2) {
      return `Set ${setNumber}: zwycięzca tie-breaka musi zgadzać się ze zwycięzcą seta.`;
    }
    return null;
  }
  return `Set ${setNumber}: wynik ${s1}:${s2} jest nieprawidłowy dla setów do ${target}.`;
}

/** Super tie-break — do 10, dwa punkty przewagi. */
function validateSuperTiebreak(row: SetRowDraft, setNumber: number): string | null {
  const s1 = parseField(row.score1);
  const s2 = parseField(row.score2);
  if (s1 === null || s2 === null || Number.isNaN(s1) || Number.isNaN(s2) || s1 < 0 || s2 < 0) {
    return `Set ${setNumber}: uzupełnij wynik obu stron.`;
  }
  const winner = Math.max(s1, s2);
  const loser = Math.min(s1, s2);
  if (winner < 10) return `Set ${setNumber}: super tie-break gra się do 10 punktów.`;
  if (winner - loser < 2) return `Set ${setNumber}: super tie-break wygrywa się dwoma punktami przewagi.`;
  return null;
}

/** Gem squasha (do 11, przewaga 2, sufit 15) albo badmintona (do 21, sufit 30). */
function validatePointsGame(
  row: SetRowDraft,
  setNumber: number,
  target: number,
  cap: number,
): string | null {
  const s1 = parseField(row.score1);
  const s2 = parseField(row.score2);
  if (s1 === null || s2 === null || Number.isNaN(s1) || Number.isNaN(s2) || s1 < 0 || s2 < 0) {
    return `Gem ${setNumber}: uzupełnij wynik obu stron.`;
  }
  const winner = Math.max(s1, s2);
  const loser = Math.min(s1, s2);

  if (winner < target) return `Gem ${setNumber}: zwycięzca musi zdobyć co najmniej ${target} punktów.`;
  if (winner === target && loser <= target - 2) return null;
  if (loser >= target - 1) {
    if (winner - loser === 2) return null;
    if (winner === cap && loser === cap - 1) return null;
  }
  return `Gem ${setNumber}: wynik ${s1}:${s2} jest nieprawidłowy.`;
}

/**
 * Waliduje pojedynczy wiersz w kontekście formularza — wybiera walidator wg
 * dyscypliny i rozpoznaje decydujący super tie-break. Jedno źródło reguł dla
 * `validateScore` i `confirmedSetWinner`.
 */
function validateRow(
  rows: readonly SetRowDraft[],
  index: number,
  rules: ScoreRules,
  discipline: DisciplineCode,
): string | null {
  const row = rows[index]!;
  const setNumber = index + 1;
  if (discipline === 'SQUASH') return validatePointsGame(row, setNumber, 11, 15);
  if (discipline === 'BADMINTON') return validatePointsGame(row, setNumber, 21, 30);
  if (isSuperTiebreakRow(rows, index, rules)) return validateSuperTiebreak(row, setNumber);
  return validateTennisSet(row, setNumber, rules.target);
}

/**
 * Sprawdza cały formularz. Zwraca polski komunikat pierwszego problemu
 * albo `null`, gdy wynik jest kompletny i poprawny.
 */
export function validateScore(
  rows: readonly SetRowDraft[],
  rules: ScoreRules,
  discipline: DisciplineCode,
): string | null {
  const filled = filledRows(rows);
  if (filled.length === 0) return 'Wpisz wynik co najmniej jednego seta.';

  // Dziura w środku formularza — np. wypełnione sety 1 i 3.
  const touchedCount = rows.filter(isRowTouched).length;
  if (touchedCount !== filled.length) {
    return 'Sety trzeba wypełniać po kolei — bez pustych w środku.';
  }

  const needed = setsToWin(rules.bestOf);
  let won1 = 0;
  let won2 = 0;

  for (let i = 0; i < filled.length; i += 1) {
    const row = filled[i]!;
    const setNumber = i + 1;

    if (setWinner(row) === null) {
      return `Set ${setNumber}: wynik nie może być remisowy.`;
    }

    const problem = validateRow(filled, i, rules, discipline);
    if (problem) return problem;

    if (setWinner(row) === 1) won1 += 1;
    else won2 += 1;

    if ((won1 === needed || won2 === needed) && i !== filled.length - 1) {
      return 'Mecz jest już rozstrzygnięty — usuń nadmiarowe sety.';
    }
  }

  if (won1 < needed && won2 < needed) {
    return needed === 1
      ? 'Mecz nie jest rozstrzygnięty — wpisz wynik decydującego seta.'
      : `Mecz nie jest rozstrzygnięty — któraś strona musi wygrać ${needed} sety(-ów).`;
  }

  return null;
}

/** Buduje ładunek `EnterScoreRequest` z wypełnionych wierszy. */
export function toScorePayload(rows: readonly SetRowDraft[]): SetScorePayload[] {
  return filledRows(rows).map((row, index) => {
    const tb1 = parseField(row.tiebreak1);
    const tb2 = parseField(row.tiebreak2);
    return {
      setNumber: index + 1,
      score1: parseField(row.score1) ?? 0,
      score2: parseField(row.score2) ?? 0,
      tiebreakScore1: tb1 !== null && !Number.isNaN(tb1) ? tb1 : null,
      tiebreakScore2: tb2 !== null && !Number.isNaN(tb2) ? tb2 : null,
    };
  });
}

/* --- Czas trwania meczu --- */

/**
 * Wyliczony czas meczu w minutach: od jawnego rozpoczęcia (`startedAt`)
 * do wpisania wyniku (`scoreEnteredAt`) — backend nie zapisuje osobnego
 * znacznika końca gry. `null`, gdy brakuje któregoś znacznika albo
 * różnica nie jest dodatnia (np. korekta wyniku po czasie).
 */
export function matchDurationMinutes(
  startedAt: string | null,
  scoreEnteredAt: string | null,
): number | null {
  if (!startedAt || !scoreEnteredAt) return null;
  const start = Date.parse(startedAt);
  const end = Date.parse(scoreEnteredAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  const minutes = Math.round((end - start) / 60_000);
  return minutes > 0 ? minutes : null;
}

/**
 * Ile minut trwa mecz, ktory wciaz sie toczy — od `startedAt` do chwili `now`.
 *
 * Odpowiednik {@link matchDurationMinutes} dla meczu bez wyniku: tam koncem
 * jest `scoreEnteredAt`, tutaj biezacy czas. `null`, gdy meczu nie rozpoczeto
 * jawnie albo znacznik startu jest z przyszlosci (rozjazd zegarow).
 */
export function elapsedMatchMinutes(
  startedAt: string | null,
  now: number = Date.now(),
): number | null {
  if (!startedAt) return null;
  const start = Date.parse(startedAt);
  if (Number.isNaN(start)) return null;

  const minutes = Math.floor((now - start) / 60_000);
  return minutes >= 0 ? minutes : null;
}

/** Formatuje minuty do postaci „1 godz. 45 min" / „45 min" / „2 godz.". */
export function formatDurationMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  if (rest === 0) return `${hours} godz.`;
  return `${hours} godz. ${rest} min`;
}

/* --- Następny mecz w drabince --- */

/** Zapowiedź kolejnego meczu zwycięzcy — panel „Następny mecz”. */
export interface NextMatchPreview {
  /** Kod rundy następnego meczu, np. `QF`. */
  round: string;
  /** Etykieta przeciwnika, jeśli druga strona slotu jest już znana. */
  opponentLabel: string | null;
}

/**
 * Znajduje slot, do którego awansuje zwycięzca tego meczu, i podaje znanego
 * przeciwnika. Zwraca `null` dla finału, meczu o 3. miejsce i meczów spoza
 * drabinki.
 */
export function nextMatchPreview(
  bracket: ApiBracket,
  match: ApiMatch,
  names: ReadonlyMap<string, string>,
): NextMatchPreview | null {
  if (!match.round || match.round === '3P' || match.bracketPosition === null) return null;

  const mainRounds = bracket.rounds
    .filter((round) => round.name !== '3P')
    .slice()
    .sort((a, b) => a.roundNumber - b.roundNumber);
  const roundIndex = mainRounds.findIndex((round) => round.name === match.round);
  if (roundIndex < 0 || roundIndex === mainRounds.length - 1) return null;

  const nextRound = mainRounds[roundIndex + 1]!;
  const nextPosition = Math.ceil(match.bracketPosition / 2);
  const nextMatch = nextRound.matches.find((item) => item.bracketPosition === nextPosition);

  // Zwycięzca wchodzi na stronę wyznaczoną parzystością pozycji — przeciwnik
  // to druga strona slotu (o ile już znany).
  const opponentIds =
    match.bracketPosition % 2 === 1 ? (nextMatch?.team2Ids ?? []) : (nextMatch?.team1Ids ?? []);
  const opponentLabel =
    opponentIds.length > 0 ? opponentIds.map((id) => names.get(id) ?? '…').join(' / ') : null;

  return { round: nextRound.name, opponentLabel };
}

/** Czy mecz-slot jest „ruszony" — rozpoczęty albo rozstrzygnięty. */
function slotTouched(slot: ApiMatch | undefined): boolean {
  return slot !== undefined && slot.status !== 'SCHEDULED' && slot.status !== 'CANCELLED';
}

/**
 * Czy korekta wyniku jest zablokowana, bo dalsza część drabinki już ruszyła.
 *
 * Lustrzane odbicie strażnika backendu (`MatchService.editScore`): zmiana
 * zwycięzcy meczu drabinkowego jest odrzucana (409), gdy slot następnej rundy —
 * a dla półfinału także mecz o 3. miejsce — jest już rozpoczęty lub rozegrany.
 * Mecze grupowe i finał nie mają dalszych slotów, więc nigdy nie są blokowane.
 */
export function isCorrectionLocked(bracket: ApiBracket, match: ApiMatch): boolean {
  if (!match.round || match.round === '3P' || match.bracketPosition === null) return false;

  const mainRounds = bracket.rounds
    .filter((round) => round.name !== '3P')
    .slice()
    .sort((a, b) => a.roundNumber - b.roundNumber);
  const roundIndex = mainRounds.findIndex((round) => round.name === match.round);
  if (roundIndex < 0) return false;

  // Przegrany półfinału schodzi do meczu o 3. miejsce — jego rozpoczęcie
  // również zamyka możliwość zmiany rozstrzygnięcia półfinału.
  if (roundIndex === mainRounds.length - 2) {
    const thirdPlace = bracket.rounds
      .find((round) => round.name === '3P')
      ?.matches.find((item) => item.status !== 'CANCELLED');
    if (slotTouched(thirdPlace)) return true;
  }

  if (roundIndex === mainRounds.length - 1) return false;

  const nextRound = mainRounds[roundIndex + 1]!;
  const nextPosition = Math.ceil(match.bracketPosition / 2);
  return slotTouched(nextRound.matches.find((item) => item.bracketPosition === nextPosition));
}
